import { useState, useEffect, useRef } from "react";
import { Trans, useTranslation } from "react-i18next";
import ReactPlayer from "react-player";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import AreasCarousel from "./components/AreasCarousel/AreasCarousel";
import "./Carreiras.scss";

gsap.registerPlugin(ScrollTrigger);

// Vídeos de fundo do hero (usam 100% da largura da página).
const HERO_VIDEO_DESKTOP =
  "https://essentialnutrition-upload-files.s3.us-east-1.amazonaws.com/site-essentiagroup/carreiras/Video_banner_desktop.aep.mp4";
const HERO_VIDEO_MOBILE =
  "https://essentialnutrition-upload-files.s3.us-east-1.amazonaws.com/site-essentiagroup/carreiras/Video_banner_mobile.aep.mp4";

// Vídeo institucional (faz a transição entre a seção preta e a branca).
const GROUP_VIDEO_URL = "https://www.youtube.com/watch?v=NF6WitHYJ98";
const PRESENTES_VIDEO_URL =
  "https://essentialnutrition-upload-files.s3.us-east-1.amazonaws.com/site-essentiagroup/carreiras/PRESENTES+FULLHD+V2.mp4";
const VIDEO_DETAIL_URL =
  "https://essentialnutrition-upload-files.s3.us-east-1.amazonaws.com/site-essentiagroup/carreiras/detalhe-fundo.png";
const STORIES_BACKGROUND_URL =
  "https://essentialnutrition-upload-files.s3.us-east-1.amazonaws.com/site-essentiagroup/carreiras/fundo-eusouessentia.png";
const MODAL_DETAIL_URL =
  "https://essentialnutrition-upload-files.s3.us-east-1.amazonaws.com/site-essentiagroup/carreiras/fundo-vertical-modal.png";
const MODAL_DETAIL_MOBILE_URL =
  "https://essentialnutrition-upload-files.s3.us-east-1.amazonaws.com/site-essentiagroup/carreiras/modal-footer-detalhe.png";
const OPPORTUNITIES_BACKGROUND_URL =
  "https://essentialnutrition-upload-files.s3.us-east-1.amazonaws.com/site-essentiagroup/carreiras/background-preto.png";
const OPPORTUNITIES_LOGO_URL =
  "https://essentialnutrition-upload-files.s3.us-east-1.amazonaws.com/site-essentiagroup/carreiras/logo-essentia.svg";

const TESTIMONIALS = [
  {
    image:
      "https://essentialnutrition-upload-files.s3.us-east-1.amazonaws.com/site-essentiagroup/carreiras/depoimento-01.png",
    quoteKey: "careers.stories.testimonials.crystiani.quote",
    modalTextKey: "careers.stories.testimonials.crystiani.modalText",
    name: "Crystiani Bohnert",
    roleKey: "careers.stories.testimonials.crystiani.role",
  },
  {
    image:
      "https://essentialnutrition-upload-files.s3.us-east-1.amazonaws.com/site-essentiagroup/carreiras/depoimento-02.png",
    quoteKey: "careers.stories.testimonials.anne.quote",
    modalTextKey: "careers.stories.testimonials.anne.modalText",
    name: "Ana Julia",
    roleKey: "careers.stories.testimonials.anne.role",
  },
  {
    image:
      "https://essentialnutrition-upload-files.s3.us-east-1.amazonaws.com/site-essentiagroup/carreiras/depoimento-03.png",
    quoteKey: "careers.stories.testimonials.leonardo.quote",
    modalTextKey: "careers.stories.testimonials.leonardo.modalText",
    name: "Leonardo Doose",
    roleKey: "careers.stories.testimonials.leonardo.role",
  },
];

// Marcas do grupo, exibidas em sequência na seção preta.
const BRANDS_BASE_URL =
  "https://essentialnutrition-upload-files.s3.us-east-1.amazonaws.com/site-essentiagroup/carreiras";
const BRANDS = [
  { key: "essentiaPharma", logo: `${BRANDS_BASE_URL}/logo-essentia-pharma.svg` },
  { key: "essentialNutrition", logo: `${BRANDS_BASE_URL}/logo-essentia-nutrition.svg` },
  { key: "noorskin", logo: `${BRANDS_BASE_URL}/logo-noorskin.svg` },
  { key: "beGenerous", logo: `${BRANDS_BASE_URL}/logo-begenerous.svg` },
  { key: "essentiaTechnologies", logo: `${BRANDS_BASE_URL}/logo-essentia-technologies.svg` },
  { key: "essentiaExperience", logo: `${BRANDS_BASE_URL}/logo-essentia-experience.svg` },
];

// Pilares culturais exibidos na seção 3 (fundo cinza claro).
const PILLARS = [
  {
    key: "harmony",
    maxWidth: 465,
    maxWidthMobile: 307,
  },
  {
    key: "innovation",
    maxWidth: 444,
    maxWidthMobile: 327,
  },
  {
    key: "connection",
    maxWidth: 480,
    maxWidthMobile: 327,
  },
  {
    key: "transformation",
    maxWidth: 413,
    maxWidthMobile: 300,
  },
  {
    key: "growth",
    maxWidth: 383,
    maxWidthMobile: 305,
  },
  {
    key: "inspiration",
    maxWidth: 389,
    maxWidthMobile: 296,
  },
];

const BENEFIT_ICON_BASE_URL =
  "https://essentialnutrition-upload-files.s3.us-east-1.amazonaws.com/site-essentiagroup/carreiras/beneficio-";
const BENEFITS = [
  { key: "wellhub" },
  { key: "healthPlan" },
  { key: "dentalPlan" },
  { key: "supplements" },
  { key: "discounts" },
  { key: "food" },
  { key: "lifeInsurance" },
  { key: "transport" },
  { key: "gifts" },
  { key: "wellbeing" },
  { key: "partnerships" },
  { key: "humanDevelopment" },
  { key: "education" },
].map((benefit, index) => ({
  ...benefit,
  icon: `${BENEFIT_ICON_BASE_URL}${String(index + 1).padStart(2, "0")}.svg`,
}));

const FAQ_ITEMS = [
  { key: "companies", maxWidth: 677 },
  { key: "workingHere", maxWidth: 607 },
  { key: "culture", maxWidth: 601 },
  { key: "benefits", maxWidth: 711 },
  { key: "areas", maxWidth: 679 },
  { key: "selection", maxWidth: 640 },
  { key: "locations", maxWidth: 557 },
  { key: "apply", maxWidth: 510 },
];

const MOBILE_QUERY = "(max-width: 767px)";

const Carreiras = () => {
  const { t } = useTranslation();

  useEffect(() => {
    const previousTitle = document.title;
    const descriptionMeta = document.querySelector('meta[name="description"]');
    const previousDescription = descriptionMeta?.getAttribute("content");

    document.title = t("careers.meta.title");
    descriptionMeta?.setAttribute(
      "content",
      t("careers.meta.description")
    );

    return () => {
      document.title = previousTitle;
      if (descriptionMeta && previousDescription !== null) {
        descriptionMeta.setAttribute("content", previousDescription);
      }
    };
  }, [t]);

  // Escolhe o vídeo conforme a viewport (evita baixar os dois).
  const [isMobile, setIsMobile] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia(MOBILE_QUERY).matches
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia(MOBILE_QUERY);
    const handleChange = (event) => setIsMobile(event.matches);
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  // Ref para o elemento da seção CEO (parallax)
  const ceoRef = useRef(null);

  useEffect(() => {
    const el = ceoRef.current;
    if (!el || isMobile) return;

    // Anima o y da imagem (não o background-position), garantindo parallax
    // real com height fixa no container e imagem maior para cobrir o movimento.
    ScrollTrigger.normalizeScroll(true);

    const img = el.querySelector(".ceo__bg");
    if (!img) return;

    const tween = gsap.fromTo(
      img,
      { yPercent: -8 },
      {
        yPercent: 8,
        ease: "none",
        scrollTrigger: {
          trigger: el,
          start: "top bottom",
          end: "bottom top",
          scrub: 2,
        },
      }
    );

    return () => {
      tween.scrollTrigger?.kill();
      tween.kill();
    };
  }, [isMobile]);

  // Estado e ref do carrossel de áreas
  const [areasActiveIndex, setAreasActiveIndex] = useState(0);
  const [areasDotsTotal, setAreasDotsTotal] = useState(10);
  const [areasDotsStep, setAreasDotsStep] = useState(1);
  const [areasIsAtEnd, setAreasIsAtEnd] = useState(false);
  const [activeTestimonial, setActiveTestimonial] = useState(null);
  const [activeBenefit, setActiveBenefit] = useState(BENEFITS[0].key);
  const [openFaqs, setOpenFaqs] = useState(() => new Set());
  const areasSliderRef = useRef(null);
  const testimonialDialogRef = useRef(null);
  const testimonialCloseRef = useRef(null);

  useEffect(() => {
    if (!activeTestimonial) return undefined;

    const previousActiveElement = document.activeElement;
    const dialog = testimonialDialogRef.current;

    const handleDialogKeyDown = (event) => {
      if (event.key === "Escape") {
        setActiveTestimonial(null);
        return;
      }

      if (event.key !== "Tab" || !dialog) return;

      const focusableElements = Array.from(
        dialog.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      ).filter((element) => !element.hasAttribute("disabled"));

      if (focusableElements.length === 0) return;

      const firstFocusableElement = focusableElements[0];
      const lastFocusableElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstFocusableElement) {
        event.preventDefault();
        lastFocusableElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastFocusableElement) {
        event.preventDefault();
        firstFocusableElement.focus();
      }
    };

    document.addEventListener("keydown", handleDialogKeyDown);
    testimonialCloseRef.current?.focus();

    return () => {
      document.removeEventListener("keydown", handleDialogKeyDown);
      if (previousActiveElement instanceof HTMLElement) {
        previousActiveElement.focus();
      }
    };
  }, [activeTestimonial]);

  const handleAreasSliderInfo = ({ dotsTotal, dotsStep }) => {
    setAreasDotsTotal(dotsTotal);
    setAreasDotsStep(dotsStep);
  };

  const heroVideo = isMobile ? HERO_VIDEO_MOBILE : HERO_VIDEO_DESKTOP;

  return (
    <main id="carreiras">
      <section className="hero">
        {/* Fundo em vídeo: decorativo, mudo e em loop para permitir autoplay.
            A key força o recarregamento ao trocar de fonte (desktop/mobile). */}
        <video
          key={heroVideo}
          className="hero__video"
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          aria-hidden="true"
        >
          <source src={heroVideo} type="video/mp4" />
        </video>

        {/* Camada escura para dar contraste ao texto */}
        <div className="hero__overlay" aria-hidden="true" />

        {/* Conteúdo limitado a 1100px (padrão da página) */}
        <div className="wrapper hero__content">
          <h1 className="hero__title">
            {t("careers.hero.titleBefore")} {t("careers.hero.titleEmphasis")}
            {t("careers.hero.titleAfter")}
          </h1>

          <p className="hero__subtitle">
            {t("careers.hero.subtitleFirst")}
            {isMobile && <br />}
            {t("careers.hero.subtitleSecond")}
            {!isMobile && <br />}
            {t("careers.hero.subtitleThird")}
          </p>

          <p className="hero__cta">{t("careers.hero.cta")}</p>
        </div>
      </section>

      {/* Seção 2 (preta): compromisso + marcas do grupo */}
      <section className="compromisso" aria-labelledby="compromisso-title">
        <div className="wrapper">
          <div className="compromisso__intro">
            <h2 id="compromisso-title" className="compromisso__title">
              {t("careers.commitment.titleLine1")}
              <br />
              {t("careers.commitment.titleLine2")}
              <br />
              {t("careers.commitment.titleLine3")}
            </h2>

            <div className="compromisso__text">
              <p>{t("careers.commitment.paragraph1")}</p>
              <p>{t("careers.commitment.paragraph2")}</p>
              <p>{t("careers.commitment.paragraph3")}</p>
            </div>
          </div>
        </div>

        <div className="compromisso__brands">
          <div className="wrapper">
            <p className="compromisso__empresas">
              {t("careers.commitment.companiesLine1")}
              <br />
              {` ${t("careers.commitment.companiesLine2")}`}
            </p>

            <ul className="compromisso__logos">
              {BRANDS.map((brand) => (
                <li key={brand.key}>
                  <img
                    src={brand.logo}
                    alt={t(`careers.brands.${brand.key}.alt`)}
                    loading="lazy"
                  />
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Vídeo em ponte: transição da seção preta para a branca (seção 3) */}
      <section
        className="video-bridge"
        aria-label={t("careers.videos.institutionalLabel")}
      >
        <div className="wrapper">
          <div className="video-bridge__frame">
            <ReactPlayer
              className="video-bridge__player"
              url={GROUP_VIDEO_URL}
              width="100%"
              height="100%"
              controls
            />
          </div>
        </div>
      </section>

      {/* Seção 3 (cinza claro): pilares culturais */}
      <section className="cultura" aria-labelledby="cultura-title">
        <div className="wrapper">
          <div className="cultura__layout">

            {/* Coluna esquerda: título âncora */}
            <div className="cultura__col-left">
              <h2 id="cultura-title" className="cultura__title">
                Trabalhe em equilíbrio.
                <br />
                Cresça com intenção.
              </h2>
              <p className="cultura__subtitle">
                {t("careers.culture.subtitle")}
              </p>
              <span className="cultura__divider" aria-hidden="true" />
            </div>

            {/* Coluna direita: lista de pilares */}
            <ul className="cultura__pillars">
              {PILLARS.map((pillar) => (
                <li key={pillar.key} className="cultura__pillar">
                  <h3 className="cultura__pillar-title">
                    {t(`careers.culture.pillars.${pillar.key}.title`)}
                  </h3>
                  <p
                    className="cultura__pillar-text"
                    style={{
                      maxWidth: isMobile ? pillar.maxWidthMobile : pillar.maxWidth,
                    }}
                  >
                    {t(`careers.culture.pillars.${pillar.key}.text`)}
                  </p>
                </li>
              ))}
            </ul>

          </div>
        </div>
      </section>

      {/* Seção 4: banner CEO */}      
      <section
        className="ceo"
        ref={ceoRef}
        aria-label={t("careers.ceo.label")}
      >
        {/* Imagem de fundo: maior que a seção para dar margem ao parallax */}
        <img
          className="ceo__bg"
          src="https://essentialnutrition-upload-files.s3.us-east-1.amazonaws.com/site-essentiagroup/carreiras/carreiras-paulo-banner.png"
          alt=""
          aria-hidden="true"
        />
        <div className="ceo__content wrapper">
          <blockquote className="ceo__quote">
            {t("careers.ceo.quote")}
          </blockquote>
          <p className="ceo__credit">
            <strong>{t("careers.ceo.name")}</strong>{" "}
            <em>{t("careers.ceo.role")}</em>
          </p>
        </div>
      </section>

      {/* Seção 5: áreas e operações */}
      <section className="areas" aria-labelledby="areas-title">
        {/* Título/subtítulo dentro do wrapper limitado a 1100px */}
        <div className="wrapper">
          <div className="areas__header">
            <h2 id="areas-title" className="areas__title">
              {t("careers.areas.titleLine1")} {" "}
              <br />
              {t("careers.areas.titleLine2")}
            </h2>
            <p className="areas__subtitle">
              {t("careers.areas.subtitleLine1")}
              <br />
              {t("careers.areas.subtitleLine2")}
            </p>
          </div>
        </div>

        {/* Carrossel: começa alinhado com o wrapper e vai até a borda direita */}
        <div
          className={`areas__carousel-outer${
            areasIsAtEnd ? " areas__carousel-outer--at-end" : ""
          }`}
        >
          <AreasCarousel
            sliderRef={areasSliderRef}
            onSlideChange={setAreasActiveIndex}
            onSliderInfoChange={handleAreasSliderInfo}
            onEndChange={setAreasIsAtEnd}
          />
        </div>

        {/* Footer (dots + botão): dentro do wrapper — alinhados ao limite de 1100px */}
        <div className="wrapper">
          <div className="areas__footer">
            <div className="areas__dots">
              {Array.from({ length: areasDotsTotal }).map((_, i) => (
                <button
                  key={i}
                  type="button"
                  className={`areas__dot${
                    Math.floor(areasActiveIndex / areasDotsStep) === i
                      ? " is-active"
                      : ""
                  }`}
                  aria-current={
                    Math.floor(areasActiveIndex / areasDotsStep) === i
                      ? "true"
                      : undefined
                  }
                  aria-label={t("careers.areas.goToCard", {
                    number: i * areasDotsStep + 1,
                  })}
                  onClick={() =>
                    areasSliderRef.current?.slickGoTo(i * areasDotsStep)
                  }
                />
              ))}
            </div>
            <a
              className="areas__cta"
              href="https://essentiagroup.gupy.io/"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("careers.areas.cta")}
            </a>
          </div>
        </div>
      </section>

      {/* Seção 6: vídeo de presentes com detalhe decorativo */}
      <section
        className="aws-video-bridge"
        aria-label={t("careers.videos.giftsLabel")}
      >
        <div
          className="aws-video-bridge__detail"
          style={{ backgroundImage: `url(${VIDEO_DETAIL_URL})` }}
          aria-hidden="true"
        />
        <div className="wrapper">
          <div className="aws-video-bridge__frame">
            <video
              className="aws-video-bridge__video"
              controls
              playsInline
              preload="metadata"
            >
              <source src={PRESENTES_VIDEO_URL} type="video/mp4" />
              {t("careers.videos.unsupported")}
            </video>
          </div>
        </div>
      </section>

      {/* Seção 7: histórias de quem cresce com a gente */}
      <section className="stories" aria-labelledby="stories-title">
        <div
          className="stories__background"
          style={{ backgroundImage: `url(${STORIES_BACKGROUND_URL})` }}
          aria-hidden="true"
        />
        <div className="wrapper">
          <h2 id="stories-title" className="stories__title">
            {t("careers.stories.titleLine1")}
            <br />
            <em>{t("careers.stories.titleEmphasis")}</em>{" "}
            {t("careers.stories.titleLine2")}
          </h2>
          <p className="stories__subtitle">
            {t("careers.stories.subtitleLine1")}
            <br />
            {t("careers.stories.subtitleLine2")}
          </p>

          <div className="stories__grid">
            {TESTIMONIALS.map((testimonial) => (
              <article className="stories__card" key={testimonial.name}>
                <div className="stories__media">
                  <img
                    className="stories__image"
                    src={testimonial.image}
                    alt={t("careers.stories.imageAlt", {
                      name: testimonial.name,
                    })}
                    loading="lazy"
                  />
                  <p className="stories__quote">{t(testimonial.quoteKey)}</p>
                  <button
                    className="stories__more"
                    type="button"
                    aria-label={t("careers.stories.openTestimonial", {
                      name: testimonial.name,
                    })}
                    onClick={() => setActiveTestimonial(testimonial)}
                  >
                    +
                  </button>
                </div>
                <h3 className="stories__name">{testimonial.name}</h3>
                <p className="stories__role">{t(testimonial.roleKey)}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Seção 8: benefícios */}
      <section className="benefits" aria-labelledby="benefits-title">
        <div className="wrapper">
          <h2 id="benefits-title" className="benefits__title">
            {t("careers.benefits.title")}
          </h2>
          <div className="benefits__grid">
            {BENEFITS.map((benefit) => (
              <article className="benefits__card" key={benefit.key}>
                <img
                  className="benefits__icon"
                  src={benefit.icon}
                  alt=""
                  aria-hidden="true"
                  loading="lazy"
                />
                <h3 className="benefits__card-title">
                  {t(`careers.benefits.items.${benefit.key}.title`)}
                </h3>
                <p className="benefits__card-text">
                  {t(`careers.benefits.items.${benefit.key}.text`)}
                </p>
              </article>
            ))}
          </div>
          <div className="benefits__accordion">
            {BENEFITS.map((benefit, index) => {
              const isOpen = activeBenefit === benefit.key;
              const panelId = `benefit-panel-${index}`;
              const triggerId = `benefit-trigger-${index}`;

              return (
                <article
                  className={`benefits__accordion-item${
                    isOpen ? " is-open" : ""
                  }`}
                  key={benefit.key}
                >
                  <button
                    id={triggerId}
                    className="benefits__accordion-trigger"
                    type="button"
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    onClick={() =>
                      setActiveBenefit(isOpen ? null : benefit.key)
                    }
                  >
                    <img
                      className="benefits__accordion-icon"
                      src={benefit.icon}
                      alt=""
                      aria-hidden="true"
                    />
                    <span className="benefits__accordion-title">
                      {t(`careers.benefits.items.${benefit.key}.title`)}
                    </span>
                    <span
                      className={`benefits__accordion-chevron${
                        isOpen ? " is-open" : ""
                      }`}
                      aria-hidden="true"
                    />
                  </button>
                  <div
                    className="benefits__accordion-panel"
                    id={panelId}
                    role="region"
                    aria-labelledby={triggerId}
                    hidden={!isOpen}
                  >
                    <p>{t(`careers.benefits.items.${benefit.key}.text`)}</p>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Penúltima seção: oportunidades */}
      <section
        className="opportunities"
        aria-labelledby="opportunities-title"
        style={{
          backgroundImage: `url(${OPPORTUNITIES_BACKGROUND_URL})`,
        }}
      >
        <div className="opportunities__content">
          <img
            className="opportunities__logo"
            src={OPPORTUNITIES_LOGO_URL}
            alt={t("careers.opportunities.logoAlt")}
          />
          <p id="opportunities-title" className="opportunities__text">
            <span>{t("careers.opportunities.textLine1")}</span>
            <strong>{t("careers.opportunities.textLine2")}</strong>
            <strong>{t("careers.opportunities.textLine3")}</strong>
          </p>
          <a
            className="opportunities__cta"
            href="https://essentiagroup.gupy.io/"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("careers.opportunities.cta")}
          </a>
        </div>
      </section>

      {/* Última seção: FAQ */}
      <section className="faq" aria-labelledby="faq-title">
        <div className="wrapper faq__wrapper">
          <h2 id="faq-title" className="faq__title">
            {t("careers.faq.title")}
          </h2>
          <div className="faq__list">
            {FAQ_ITEMS.map((item, index) => {
              const isOpen = openFaqs.has(index);
              const answerId = `faq-answer-${index}`;
              const questionId = `faq-question-${item.key}`;
              const question = t(`careers.faq.items.${item.key}.question`);

              return (
                <article className="faq__item" key={item.key}>
                  <button
                    id={questionId}
                    className="faq__question"
                    type="button"
                    aria-expanded={isOpen}
                    aria-controls={answerId}
                    aria-label={t(
                      isOpen
                        ? "careers.faq.collapse"
                        : "careers.faq.expand",
                      { question }
                    )}
                    onClick={() => {
                      setOpenFaqs((currentOpenFaqs) => {
                        const nextOpenFaqs = new Set(currentOpenFaqs);
                        if (nextOpenFaqs.has(index)) {
                          nextOpenFaqs.delete(index);
                        } else {
                          nextOpenFaqs.add(index);
                        }
                        return nextOpenFaqs;
                      });
                    }}
                  >
                    <span className="faq__question-label">{question}</span>
                    <span className="faq__toggle" aria-hidden="true">
                      {isOpen ? "−" : "+"}
                    </span>
                  </button>
                  <div
                    className="faq__answer"
                    id={answerId}
                    role="region"
                    aria-labelledby={questionId}
                    hidden={!isOpen}
                  >
                    <p style={{ maxWidth: item.maxWidth }}>
                      {t(`careers.faq.items.${item.key}.answer`)}
                    </p>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {activeTestimonial && (
        <div
          className="testimonial-modal__overlay"
          role="presentation"
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              setActiveTestimonial(null);
            }
          }}
        >
          <div
            className="testimonial-modal"
            ref={testimonialDialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="testimonial-modal-title"
          >
            <div
              className="testimonial-modal__detail"
              style={{
                "--modal-detail-desktop": `url(${MODAL_DETAIL_URL})`,
                "--modal-detail-mobile": `url(${MODAL_DETAIL_MOBILE_URL})`,
              }}
              aria-hidden="true"
            />
            <div className="testimonial-modal__content">
              <div className="testimonial-modal__identity">
                <h2 id="testimonial-modal-title">
                  {activeTestimonial.name}
                </h2>
                <p>{t(activeTestimonial.roleKey)}</p>
              </div>
              <p className="testimonial-modal__text">
                {"“"}
                <Trans
                  i18nKey={activeTestimonial.modalTextKey}
                  components={{ br: <br /> }}
                />
                {"”"}
              </p>
            </div>
            <button
              ref={testimonialCloseRef}
              className="testimonial-modal__close"
              type="button"
              aria-label={t("careers.stories.closeTestimonial")}
              onClick={() => setActiveTestimonial(null)}
            >
              −
            </button>
          </div>
        </div>
      )}
    </main>
  );
};

export default Carreiras;
